import { assertNever } from './common/TypeScriptUtility';

/**
 * This type represents values with two possibilities. A value of type `Either<L, R>` represents a value of type `L` or `R`. It is either a
 * value of type `L` (represented as `Left<L>`) or a value of type `R` (represented as `Right<R>`).
 */
export type Either<L, R> = Left<L> | Right<R>;

export interface Left<T> {
  readonly type: 'Left';
  readonly value: T;
}

export interface Right<T> {
  readonly type: 'Right';
  readonly value: T;
}

/**
 * Smart constructor for creating a left value.
 *
 * @param value - The left value.
 */
export function left<L, R>(value: L): Either<L, R> {
  return {
    type: 'Left',
    value
  };
}

/**
 * Smart constructor for creating a right value.
 *
 * @param value - The right value.
 */
export function right<L, R>(value: R): Either<L, R> {
  return {
    type: 'Right',
    value
  };
}

/**
 * Higher-order helper function for either values.
 *
 * @param f - Function to apply to a left value.
 * @param g - Function to apply to a right value.
 */
export function either<L, R, T>(f: (l: L) => T, g: (r: R) => T): (e: Either<L, R>) => T {
  return (e: Either<L, R>) => {
    switch (e.type) {
      case 'Left': return f(e.value);
      case 'Right': return g(e.value);
      default: return assertNever(e);
    }
  };
}

/**
 * Returns a left value or a given default if applied to a right value.
 *
 * @param l - Default value.
 */
export function fromLeft<L, R>(l: L): (e: Either<L, R>) => L {
  return either(v => v, _ => l);
}

/**
 * Returns a right value or a given default if applied to a left value.
 *
 * @param r - Default value.
 */
export function fromRight<L, R>(r: R): (e: Either<L, R>) => R {
  return either(_ => r, v => v);
}

/**
 * Checks if a given either value is left.
 *
 * @param e - The either value to check.
 */
export function isLeft<L, R>(e: Either<L, R>): boolean {
  return either(_ => true, _ => false)(e);
}

/**
 * Checks if a given either value is right.
 *
 * @param e - The either value to check.
 */
export function isRight<L, R>(e: Either<L, R>): boolean {
  return !isLeft(e);
}
